import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, withRouter } from 'react-router-dom';
import * as PropTypes from 'prop-types';

import {
    fetchingUserClients,
    fetchUserClientsFailed,
    fetchUserClientsSuccess,
    setUserInfo,
    userDetailsRefreshed
} from '../../actions/AuthActions';
import { LoadingOverlay } from '../LoadingOverlay/LoadingOverlay';
import {
    getClaimsFromToken,
    getLwtUser, getToken
} from '../../functions/AuthenticationFunctions';
import Routes from '../../constants/Routes';
import { useServiceApi } from '../../hooks/services';
import { featuresServiceApiFactory } from '../../api/featuresServiceApiFactory';
import { fetchFeaturesFail, fetchFeaturesSuccess, fetchingFeatures } from '../../actions/SecurityGroupActions';
import {
    failedToRetrievePermissions,
    gettingPermissions,
    retrievedPermissions
} from '../../actions/AuthorizationActions';
import { permissionsServiceApiFactory } from '../../api/permissionsServiceApiFactory';
import { authServiceApiFactory } from '../../auth/authServiceApiFactory';
import { getApiError } from '../../api/CommonFunctions';

const AuthenticatedRouteContainer = props => {
    const {
        children
    } = props;
    const featuresServiceApi = useServiceApi(featuresServiceApiFactory);
    const permissionsServiceApi = useServiceApi(permissionsServiceApiFactory);
    const authServiceApi = useServiceApi(authServiceApiFactory);

    const {
        features,
        isFetching: featuresLoading,
        error: featuresError
    } = useSelector(state => state.securityGroup);
    const {
        permissions,
        isFetching: permissionsLoading,
        error: permissionError
    } = useSelector(state => state.authorizationReducer);
    const {
        userClients,
        isLoading: clientsLoading,
        error: clientsError
    } = useSelector(state => state.authenticationReducer);
    const refreshUserDetails = useSelector(state => state.authenticationReducer.refresh);
    const user = useSelector(state => state.authenticationReducer.user);

    const dispatch = useDispatch();

    const [lwtClaims, setLwtClaims] = useState(null);
    const fetchFeatures = !featuresLoading && featuresError == null;
    const fetchPermissions = !permissionsLoading && permissionError == null;
    const fetchClients = !clientsLoading && clientsError == null;

    const setUserData = () => {
        const token = getToken();
        if (token) {
            setLwtClaims(getClaimsFromToken(token));
            fetchFeatures && getClientFeatures();
            fetchPermissions && getUserPermissions();
            fetchClients && getUserClients();
        }
    };

    useEffect(() => {
        setUserData();
    }, []);

    useEffect(() => {
        if (refreshUserDetails) {
            dispatch(setUserInfo(null));
            setUserData();
            dispatch(userDetailsRefreshed());
        }
    }, [refreshUserDetails]);

    useEffect(() => {
        if (getToken()
            && user == null
            && permissions != null
            && lwtClaims?.uid != null
            && lwtClaims?.cid != null
            && features != null
        ) {
            dispatch(setUserInfo(getLwtUser(permissions)));
        }
    }, [lwtClaims, features, permissions]);

    const getClientFeatures = () => {
        dispatch(fetchingFeatures());
        featuresServiceApi.getClientFeatures().then(response => {
            if (response.error) {
                dispatch(fetchFeaturesFail(response.error));
            } else {
                dispatch(fetchFeaturesSuccess(response.features));
            }
        });
    };

    const getUserPermissions = () => {
        dispatch(gettingPermissions());
        permissionsServiceApi.getUserPermissions().then(response => {
            if (response.error) {
                if (response.error) {
                    dispatch(
                        failedToRetrievePermissions({
                            code: 502,
                            message: 'An unknown error occurred while fetching permissions'
                        })
                    );
                } else {
                    dispatch(
                        failedToRetrievePermissions({
                            code: 500,
                            message: 'An unknown error occurred while fetching permissions'
                        })
                    );
                }
            } else {
                dispatch(retrievedPermissions(response.permissions));
            }
        });
    };

    const getUserClients = () => {
        dispatch(fetchingUserClients());
        authServiceApi.getUserClients().then(response => {
            if (!response.error) {
                dispatch(
                    fetchUserClientsSuccess(
                        response && response.userClients
                            ? response.userClients
                            : response
                    )
                );
            } else {
                dispatch(fetchUserClientsFailed(getApiError(response.error)));
            }
        });
    };

    if (!getToken()) {
        return <Redirect to={Routes.LOGIN} />;
    } else if (lwtClaims && lwtClaims.cid == null) {
        return <Redirect to={Routes.CLIENT_SELECT} />;
    } else if (user != null
        && (permissions != null || permissionError != null)
        && (features != null || featuresError != null)
        && (userClients != null || clientsError != null)
    ) {
        return children;
    } else {
        return <LoadingOverlay loading />;
    }
};

AuthenticatedRouteContainer.propTypes = {
    children: PropTypes.array.isRequired
};

export default withRouter(AuthenticatedRouteContainer);